import { Box, Switch, IconButton } from "@mzc-pdc/ui";
import { Typography } from "@components_v2/typography/typography";
import { IconCancel, IconInfoCircle, IconMoreHoriz, IconSetting } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Menu } from "@components_v2/menu/menu";
import { useMemo } from "react";
import Tooltip from "@components_v2/Tooltip/tooltip";

export const NotifyHeader = ({
    isUnreadOnly,
    onClose,
    onToggleUnread,
    onClickMarkAllAsRead,
    isDevEnvironment,
    useTestData,
    onToggleTestMode,
    selectedTestCase,
    onTestCaseChange,
}) => {
    const { t } = useTranslation();

    const testCaseOptions = useMemo(
        () => [
            { value: "empty", label: t("common::label::Empty notifications") },
            { value: "unreadOnly", label: t("common::label::Unread only") },
            { value: "readOnly", label: t("common::label::Read only") },
            { value: "mixedStatus", label: t("common::label::Mixed read/unread") },
            { value: "allTypes", label: t("common::label::All comment types mixed") },
            { value: "allDomains", label: t("common::label::All domains mixed") },
            { value: "allAssetMediaTypes", label: t("common::label::All asset media types mixed") },
        ],
        [t],
    );

    const menuList = useMemo(
        () => [
            {
                label: t("common::label::Mark all as read", "Mark all as read"),
                onClick: () => {
                    onClickMarkAllAsRead?.();
                },
                clickClose: true,
            },
        ],
        [t, onClickMarkAllAsRead],
    );

    const testMenuList = useMemo(() => {
        const testMenuItems = [
            {
                label: useTestData
                    ? t("common::label::Turn off test mode")
                    : t("common::label::Turn on test mode"),
                onClick: () => {
                    onToggleTestMode?.();
                },
                clickClose: true,
            },
        ];

        if (useTestData) {
            testCaseOptions.forEach((option) => {
                testMenuItems.push({
                    label: option.label + (selectedTestCase === option.value ? " ✓" : ""),
                    onClick: () => {
                        onTestCaseChange?.(option.value);
                    },
                    clickClose: true,
                });
            });
        }

        return testMenuItems;
    }, [t, useTestData, onToggleTestMode, selectedTestCase, onTestCaseChange, testCaseOptions]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "56px",
                px: 1.5,
                backgroundColor: alpha(theme.palette.common.white, 0.6),
                borderBottom: `1px solid ${theme.palette.greySecondary[100]}`,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        gap: 0.25,
                        px: 1,
                    }}
                >
                    <Typography
                        variant="subtitle1_700"
                        sx={{
                            color: theme.palette.greySecondary.main,
                            fontSize: "16px",
                            lineHeight: 1.5,
                        }}
                    >
                        {t("common::label::Notifications", "Notifications")}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                        }}
                    >
                        <Tooltip
                            title={t(
                                `common::msg::Notifications will be deleted after 7 days.`,
                                `Notifications will be deleted after 7 days.`,
                            )}
                            placement={"top"}
                        >
                            <span>
                                <IconInfoCircle size={16} color={theme.palette.greySecondary.main} />
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                {isDevEnvironment && (
                    <Menu
                        menuWidth={160}
                        placement={"right"}
                        type={"icon"}
                        label={<IconSetting size={16} />}
                        color={"inherit"}
                        size={"medium"}
                        list={testMenuList}
                    />
                )}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <Typography
                        variant="body3_400"
                        sx={{
                            color: theme.palette.greySecondary.main,
                            fontSize: "12px",
                            lineHeight: 1.5,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {t("common::label::Show only unread items", "Show only unread items")}
                    </Typography>
                    <Switch
                        checked={isUnreadOnly}
                        onChange={(e) => onToggleUnread?.(e.target.checked)}
                        sx={{ mx: theme.spacing(-1) }}
                    />
                </Box>
                <Menu
                    menuWidth={160}
                    placement={"right"}
                    type={"icon"}
                    label={<IconMoreHoriz size={16} />}
                    color={"inherit"}
                    size={"medium"}
                    list={menuList}
                />
                <Tooltip title={t(`common::label::Close`, `Close`)}>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "100px",
                        }}
                    >
                        <IconCancel size={16} color={theme.palette.greySecondary.main} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};
